
import { Task } from "@/types/task";
import TaskDetails from "./TaskDetails";
import TaskActions from "./TaskActions";
import CommentCount from "./CommentCount";
import { useIsMobile } from "@/hooks/use-mobile";
import { Checkbox } from "@/components/ui/checkbox";
import { useDraggable } from "@dnd-kit/core";
import { TaskActionProps } from "../types/componentProps";

interface TaskItemProps extends TaskActionProps {
  commentCount: number | undefined;
  draggable?: boolean;
}

const TaskItem = ({
  task,
  commentCount,
  onEditClick,
  onDeleteClick,
  onCalendarClick,
  onCompleteClick,
  onSkipClick,
  onMoveClick,
  draggable = false,
}: TaskItemProps) => {
  const _isMobile = useIsMobile();

  const dragData = useDraggable({
    id: task.id,
    data: {
      importance: task.importance,
      urgency: task.urgency
    },
    disabled: !draggable,
  });

  const { attributes, listeners, setNodeRef, transform } = draggable
    ? dragData
    : { attributes: {}, listeners: {}, setNodeRef: () => {}, transform: null };

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: 0.5,
    transition: 'transform 0.01s, opacity 0.2s ease-in-out',
    willChange: 'transform',
    cursor: 'grabbing',
  } : undefined;

  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !task.completed;

  const handleClick = () => {
    const isDragging = !!transform;
    if (!isDragging) {
      onEditClick(task);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const isDragging = !!transform;
      if (!isDragging) {
        onEditClick(task);
      }
    }
  };

  return (
    <div
      ref={draggable ? setNodeRef : undefined}
      style={style}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      className={`group flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-5 bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-100/50 shadow-soft hover:shadow-lg hover:bg-white/95 hover:scale-[1.01] transition-all duration-200 ease-in-out gap-3 sm:gap-4 ${
        draggable ? 'cursor-grab active:cursor-grabbing hover:-translate-y-1' : 'cursor-default hover:-translate-y-0.5'
      } ${
        isOverdue ? "border-l-4 border-l-red-500 bg-gradient-to-r from-red-50/50 to-white/70 hover:border-l-red-600" : ""
      } ${
        task.completed ? "opacity-75 bg-gradient-to-r from-green-50/30 to-white/50" : ""
      } transform-gpu animate-fade-in`}
    >
      <div
        className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0 w-full"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="flex-shrink-0 mt-1">
          <Checkbox
            checked={task.completed}
            onCheckedChange={(checked) => {
              if (checked === true) onCompleteClick(task);
            }}
            disabled={task.completed || !onCompleteClick}
            className="w-5 h-5 border-2 border-gray-300 data-[state=checked]:bg-gradient-primary data-[state=checked]:border-indigo-500 transition-all duration-200"
          />
        </div>
        <TaskDetails 
          task={task} 
          onTaskClick={() => onEditClick(task)}
          isOverdue={isOverdue}
          checkbox={null}
        />
      </div>
      <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto justify-end mt-2 sm:mt-0 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
        <CommentCount count={commentCount} />
        <TaskActions
          task={task}
          onDeleteClick={onDeleteClick}
          onCalendarClick={onCalendarClick}
          onSkipClick={onSkipClick}
          onMoveClick={onMoveClick}
          showLabels={false}
        />
      </div>
    </div>
  );
};

export default TaskItem;
